/**
 * Soul Food pipeline status widget for the Admin Dashboard.
 * Shows last generation + last publish timestamps for both Soul Food and
 * Weekly Reflections, with a red dot when anything is overdue (>8 days).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sprout, RefreshCw, AlertCircle, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatRelative = (iso) => {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const diffSec = (Date.now() - d.getTime()) / 1000;
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
};

const StatusRow = ({ label, value, overdue }) => (
  <div className="flex items-center justify-between text-sm py-1.5">
    <span className="text-gray-600">{label}</span>
    <div className="flex items-center gap-2">
      <span className={`font-medium ${overdue ? 'text-red-600' : 'text-forest'}`}>{value}</span>
      <span
        className={`inline-block w-2 h-2 rounded-full ${overdue ? 'bg-red-500' : 'bg-emerald-500'}`}
        aria-hidden="true"
      />
    </div>
  </div>
);

const SoulFoodPipelineStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/soul-food/pipeline-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  return (
    <Card className="card-base" data-testid="soul-food-pipeline-status">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
            <Sprout className="h-5 w-5" />
            Soul Food Pipeline
            {status && (
              status.healthy ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200">
                  <Check className="h-3 w-3" /> Healthy
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-medium border border-red-200">
                  <AlertCircle className="h-3 w-3" /> Overdue
                </span>
              )
            )}
          </CardTitle>
          <button
            onClick={fetchStatus}
            disabled={loading}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-forest transition-colors disabled:opacity-50"
            data-testid="soul-food-pipeline-refresh"
            title="Refresh status"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : !status ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Soul Food</p>
              <StatusRow
                label="Last generated"
                value={formatRelative(status.soul_food.last_generated_at)}
                overdue={status.soul_food.generation_overdue}
              />
              <StatusRow
                label="Last published"
                value={formatRelative(status.soul_food.last_published_at)}
                overdue={status.soul_food.publish_overdue}
              />
              {status.soul_food.last_published_title && (
                <p className="text-xs text-gray-500 mt-1 italic truncate">
                  "{status.soul_food.last_published_title}"
                </p>
              )}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Weekly Reflections</p>
              <StatusRow
                label="Last generated"
                value={formatRelative(status.weekly_reflections.last_generated_at)}
                overdue={status.weekly_reflections.generation_overdue}
              />
              <StatusRow
                label="Last published"
                value={formatRelative(status.weekly_reflections.last_published_at)}
                overdue={status.weekly_reflections.publish_overdue}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SoulFoodPipelineStatus;
